<script lang="ts">
  import FicheProjetContrats from '@/generated/fiches/FicheProjetContrats.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import { storeCreneau, storePaie, storeEtablissement, storeRubrique, storeMetaAttrib } from '@/generated/stores';
  import { ContratDpaeStatuts, ContratStatuts } from '@/generated/classes/Contrat';
  import { PaieStatuts } from '@/generated/classes/Paie';
  import Etablissement, { EtablissementPoleEmploiStatuts } from '@/generated/classes/Etablissement';
  import FicheBase from '@/framework/base/components/FicheBase';
  import MetaAttrib from '@/generated/classes/MetaAttrib';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { defineComponent } from "vue";
  import { ListFilter, ColumnFilter, ListChoix } from '../classes/ListData';
  import Contrat from '@/extend/classes/Contrat';
  import { goToModule } from '@/framework/base/helpers/NavHelper';
  import { takeSalarieContratReturn, setPendingReopenSalarieContrats } from '@/custom/helpers/salarieContratReturn';
  import Route from '@/extend/classes/Route';
  import { Fiches, Modules } from '@/generated/classes/AppSchema';

  export default defineComponent({
    components: {},
    extends: FicheProjetContrats,
    emits: ['back'],
    data() {
      const lastContextId = undefined as number | undefined;
      const lastSection = undefined as number | undefined;
      const previousLastSection = undefined as number | undefined;

      return {
        mContratId: 0,
        displayedSections: {} as Record<string, boolean>,
        previousLastSection,
        lastSection,
        lastContextId
      };
    },
    computed: {
      etablissement(): Etablissement {
        return storeEtablissement.getEtablissement(this.contrat.etablissementId);
      },
      contratId(): Number {
        if (this.mContratId != this.contrat.id) {
          this.mContratId = this.contrat.id;
          this.loadPaiesCreneaux();
        }
        return this.contrat.id
      }
    },
    methods: {
      // "Retour" : si on a ouvert ce contrat depuis la liste des contrats d'un salarié, y revenir
      // (navigation inter-module, non gérée par ModuleBase.back). On repasse d'abord par la fiche
      // Salarié pour reconstruire la pile d'historique du module SALARIE (détruite par l'aller-
      // retour) : la fiche Salarié rouvre ensuite la liste par-dessus, si bien que le "Retour" de
      // la liste revient nativement à la fiche Salarié. Sinon comportement normal (émission 'back').
      back(): void {
        const origin = this.contrat ? takeSalarieContratReturn(this.contrat.id) : null;
        if (origin) {
          const salarie = origin.data ? origin.data.propData : null;
          if (salarie && salarie.id) {
            const salarieRoute = new Route();
            salarieRoute.module = Modules.SALARIE;
            salarieRoute.fiche = Fiches.SALARIE_ABSTRACT;
            salarieRoute.ficheObjId = salarie.id;
            setPendingReopenSalarieContrats(origin);   // la liste sera rouverte par la fiche Salarié
            goToModule(this.router, salarieRoute);
          } else {
            goToModule(this.router, origin);            // fallback : au moins revenir à la liste
          }
        } else {
          this.$emit('back');
        }
      },
      ficheMounted() {
        this.loadPaiesCreneaux();
        const allData = new ListFilter();
        allData.pageLimit = -1;
        storeRubrique.listFilteredRubrique(allData);
        storeMetaAttrib.listFilteredMetaAttrib(allData);
        this.onShowSectionChanged();
        this.setFocus();
      },
      loadPaiesCreneaux() {
        const filter = new ListFilter();
        const columnFilter = new ColumnFilter();
        const choix = new ListChoix();
        choix.value = this.contrat.id;
        columnFilter.name = "contratId";
        columnFilter.staticFilter = true;
        columnFilter.filters.push(choix);
        filter.columnsFilter.push(columnFilter);
        storePaie.listFilteredPaie(filter);
        storeCreneau.listFilteredCreneau(filter);
      },
      isDisplayed(value: string): boolean {
        if (this.contratId < 0 && value == 'CONTRAT_SALARIE') {
          this.setExtended(new Extended('CONTRAT_SALARIE', this.fiche, true));
        }
        let res = true;
        if (value == 'CONTRAT_SALARIE') {
          res = this.contrat.signataireId > 0 && this.contrat.signatorySaved;
        } else if (value == "CRENEAUX") {
          res = this.contrat.emploiId > 0 && this.contrat.emploiNom != '' && this.contrat.conventionAppliqueeId > 0;
        } else if (value == "REMUNERATION") {
          res = this.contrat.creneauxValid;
        } else if (value == "AFFILIATION_PROCESS") {
          res = this.contrat.statut >= ContratStatuts.COMPUTED &&
            this.etablissement.poleEmploiStatut < EtablissementPoleEmploiStatuts.NOT_SHOW;
        } else if (value == "CONTRAT") {
          res = this.contrat.statut >= ContratStatuts.COMPUTED;
        } else if (value == "VALUATE_META_ATTRIB") {
          res = this.contrat.statut >= ContratStatuts.COMPUTED && this.contrat.metaValues.length != 0;
        } else if (value == "DPAE") {
          res = this.contrat.dpaeStatut > ContratDpaeStatuts.INITIAL;
        } else if (value == "PAIES") {
          res = false;
          const paies = storePaie.listPaie.filter(a => a.contratId == this.contrat.id);
          paies.forEach((paie) => {
            if (this.contrat.dpaeStatut > ContratDpaeStatuts.PROPOSEE && this.contrat.dpaeStatut != ContratDpaeStatuts.OBLIGATOIRE &&
              paie != undefined && paie.statut >= PaieStatuts.GENERATED) {
              res = true;
            }
          });
        }

        if (this.displayedSections[value] !== res) {
          this.displayedSections[value] = res;
          this.setDisplayed(value, res);
        }


        return res;
      },
      setDisplayed(value: string, res: boolean) {
        if (this.lastSection === this.sections.indexOf(value) && res) {
          return;
        }

        if (value === this.sections[0]) {
          if (this.contrat.id !== this.lastContextId) {
            this.previousLastSection = -1;
            this.lastContextId = this.contrat.id;
          } else {
            if (this.previousLastSection && this.lastSection && this.previousLastSection < this.lastSection) {
              this.previousLastSection = this.lastSection;
            }
          }
          this.lastSection = 0;
        } else if (value == this.sections[this.sections.length - 1]) {
          if (res) {
            this.lastSection = this.sections.indexOf(value);
          }
          if (this.previousLastSection && this.lastSection && this.lastSection != this.previousLastSection &&
            this.previousLastSection != -1 && this.previousLastSection != this.sections.length - 1) {
            const lastRef = this.$refs[this.sections[this.previousLastSection]];
            if (lastRef) {
              // @ts-expect-error: ref exists
              const section = lastRef.$el.getBoundingClientRect();
              // @ts-expect-error: ref exists
              const module = this.$parent.$parent.$el.getBoundingClientRect();
              const delta = module.bottom - section.bottom;
              if (delta < 50 && this.lastSection > this.previousLastSection) {
                debugger
                FicheBase.scrollFiche();
              }
            }
          }
        } else {
          if (res) {
            this.lastSection = this.sections.indexOf(value);
          }
        }
      },
      filterMetaAttrib(metaAttrib: MetaAttrib[]): MetaAttrib[] {
        const filterMetaAttrib = metaAttrib.filter(a => {
          return (
            a.classe == 'CONTRAT' &&
            (a.contratTemplateId == this.contrat.contratTemplateId) &&
            (a.conventionIds.includes(this.contrat.conventionAppliqueeId) || a.conventionIds.length == 0) &&
            (a.emploiIds.includes(this.contrat.emploiId) || a.emploiIds.length == 0)
          )
        });
        filterMetaAttrib.forEach((meta: MetaAttrib) => {
          meta.docObjType = ObjTypes.CONTRAT;
          meta.docObjId = this.contrat.id;
        });
        return filterMetaAttrib;
      }
    }
  })

</script>
